import {
  APP_NAME,
  type EventSummary,
  type EventTemplate,
  type FollowedEvent,
  LINK_PATH,
  MAX_EVENT_TITLE,
  MAX_SENDER_NAME,
  type MeResponse,
  PRODUCTION_ORIGIN,
  SUPPORT_CONTACT,
  templates,
} from '@eventping/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowRight,
  Bell,
  CalendarCheck,
  ChevronRight,
  Clock,
  Link2,
  LogOut,
  MessageCircle,
  Plus,
  ShieldCheck,
  Smartphone,
  Users,
} from 'lucide-react'
import { useState } from 'react'
import { CreditBadge, CreditBanner } from '@/components/credit'
import {
  Card,
  ChannelsNotice,
  FieldError,
  Footer,
  Page,
  timeOf,
  Wordmark,
} from '@/components/shell'
import { TemplateChips, TemplatePreview } from '@/components/templates'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiFailure, api, signOut, useMe } from '@/lib/api'

export function HomePage() {
  const me = useMe()
  if (me.isPending) return <Page>{null}</Page>
  if (me.isError) return <Page>Could not reach the server.</Page>
  return me.data.user ? <EventsPage me={me.data} /> : <Landing me={me.data} />
}

export function CreatorHeader({ me }: { me: MeResponse }) {
  return (
    <>
      <Link to="/" aria-label={`${APP_NAME} home`}>
        {/* On narrow phones the balance badge needs the room: the mark alone, then. */}
        <Wordmark compact={Boolean(me.user)} />
      </Link>
      <div className="ml-auto flex items-center gap-1">
        {me.user && me.textsEnabled && <CreditBadge />}
        {me.devTools && (
          <Button asChild variant="ghost" size="sm">
            <Link to="/dev/phones">
              <Smartphone /> <span className="max-[419px]:sr-only">Phones</span>
            </Link>
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => void signOut('local')}>
          <LogOut className="min-[420px]:hidden" aria-hidden />
          <span className="max-[419px]:sr-only">Sign out</span>
        </Button>
      </div>
    </>
  )
}

const SAMPLE_STEP = templates.step(
  { appName: APP_NAME, origin: PRODUCTION_ORIGIN, support: SUPPORT_CONTACT },
  {
    prefix: '',
    event: 'House move',
    step: 'Keys handed over',
    note: 'We are in!',
    link: `${PRODUCTION_ORIGIN}${LINK_PATH}Ab3dE7hJk9mQ`,
  },
)

/**
 * The public front page at eventping.app: what EventPing is and does, under its own name (the
 * DBA toll-free verification checks the site against), then sign-in.
 */
function Landing({ me }: { me: MeResponse }) {
  // Texts-off mode: the same page, about email (the opt-in proof pages still show texting).
  const texts = me.textsEnabled
  return (
    <Page
      header={
        <>
          <Wordmark />
          <Button asChild variant="ghost" size="sm" className="ml-auto">
            {me.auth === 'oidc' ? (
              <a href="/auth/login?returnTo=%2F">Sign in</a>
            ) : (
              <a href="#sign-in">Sign in</a>
            )}
          </Button>
        </>
      }
    >
      <section className="pt-4 pb-8">
        <p className="text-sm font-semibold tracking-wide text-primary uppercase">{APP_NAME}</p>
        <h1 className="mt-2 text-4xl leading-tight font-semibold text-balance sm:text-5xl">
          {texts
            ? 'Step-by-step text updates for any event.'
            : 'Step-by-step updates for any event.'}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          Moving house, running a launch, planning a family day? {APP_NAME} lets you plan the steps,
          share one sign-up link, and tap once as each step happens. Everyone who signed up gets a
          short {texts ? 'text' : 'email'} with a link to a live checklist and a shared
          conversation.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button asChild size="lg">
            {me.auth === 'oidc' ? (
              <a href="/auth/login?returnTo=%2F">
                Start an event <ArrowRight />
              </a>
            ) : (
              <a href="#sign-in">
                Start an event <ArrowRight />
              </a>
            )}
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link to="/opt-in">How opt-in works</Link>
          </Button>
        </div>
      </section>

      <section aria-labelledby="how" className="mb-8">
        <h2 id="how" className="mb-3 text-xl font-semibold">
          How it works
        </h2>
        <ol className="grid gap-3 sm:grid-cols-3">
          {[
            {
              icon: CalendarCheck,
              title: 'Plan the steps',
              text: 'List what will happen, from first to last: “Truck loaded”, “On the road”, “Keys handed over”.',
            },
            {
              icon: Link2,
              title: 'Share your join link',
              text: texts
                ? `People sign up by email or text, with their own number and consent. You approve who joins. ${APP_NAME} never messages anyone first.`
                : `People sign up with their own email address. You approve who joins. ${APP_NAME} never messages anyone first.`,
            },
            {
              icon: Bell,
              title: 'Tap as it happens',
              text: texts
                ? 'One tap notifies everyone who signed up. Replies by text land in one shared thread.'
                : 'One tap notifies everyone who signed up. Replies land in one shared thread on the event page.',
            },
          ].map(({ icon: Icon, title, text }) => (
            <li key={title} className="rounded-2xl bg-card p-4 shadow-xs">
              <Icon className="mb-2 size-6 text-primary" aria-hidden />
              <p className="font-semibold">{title}</p>
              <p className="mt-1 text-sm text-muted-foreground">{text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section aria-labelledby="sample" className="mb-8 grid gap-4 sm:grid-cols-2">
        <Card>
          <h2 id="sample" className="mb-3 flex items-center gap-2 text-lg font-semibold">
            <MessageCircle className="size-5 text-primary" aria-hidden /> A typical{' '}
            {texts ? 'text' : 'update'}
          </h2>
          <p className="rounded-2xl rounded-bl-md bg-secondary px-4 py-2.5 text-sm break-words whitespace-pre-wrap">
            {SAMPLE_STEP}
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            {texts
              ? 'One short text per step, never marketing. Message frequency varies.'
              : 'One short email per step, never marketing. Every email has an unsubscribe link.'}
          </p>
        </Card>
        {texts ? (
          <Card>
            <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold">
              <ShieldCheck className="size-5 text-primary" aria-hidden /> Getting texts from{' '}
              {APP_NAME}?
            </h2>
            <p className="text-sm">
              You signed up on an event’s join page. Reply <strong>STOP</strong> to any text to opt
              out, or <strong>HELP</strong> for help. Msg & data rates may apply. Questions:{' '}
              {me.support}.
            </p>
            <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
              <Link to="/opt-in" className="underline">
                How opt-in works
              </Link>
              <Link to="/recover" className="underline">
                Lost your link?
              </Link>
            </p>
          </Card>
        ) : (
          <Card>
            <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold">
              <ShieldCheck className="size-5 text-primary" aria-hidden /> Getting updates from{' '}
              {APP_NAME}?
            </h2>
            <p className="text-sm">
              You signed up on an event’s join page. Every email has a one-click unsubscribe link.
              Questions: {me.support}.
            </p>
            <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
              <Link to="/recover" className="underline">
                Lost your link?
              </Link>
            </p>
          </Card>
        )}
      </section>

      <Card id="sign-in" className="scroll-mt-20">
        {me.auth === 'oidc' ? (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold">Sign in to start an event</h2>
            <Button asChild size="lg" className="w-full">
              <a href="/auth/login?returnTo=%2F">
                Sign in <ArrowRight />
              </a>
            </Button>
          </div>
        ) : (
          <DevSignIn />
        )}
      </Card>
      <Footer />
    </Page>
  )
}

/** AUTH_MODE=dev only, which the API refuses in production: a stand-in for the real sign-in. */
function DevSignIn() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const client = useQueryClient()

  async function devSignIn(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await api('/auth/dev-login', { body: { name, email } })
      await client.invalidateQueries({ queryKey: ['me'] })
    } catch (err) {
      setError((err as Error).message)
    }
  }

  return (
    <form className="space-y-4" onSubmit={devSignIn}>
      <div>
        <h2 className="text-lg font-semibold">Sign in (local development)</h2>
        <p className="text-sm text-muted-foreground">
          A stand-in for the real sign-in, which needs the identity provider. Anything works.
        </p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="dev-name">Your name</Label>
        <Input
          id="dev-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoComplete="given-name"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="dev-email">Email (optional)</Label>
        <Input
          id="dev-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <FieldError>{error}</FieldError>
      <Button type="submit" size="lg" className="w-full">
        Continue <ArrowRight />
      </Button>
    </form>
  )
}

function EventsPage({ me }: { me: MeResponse }) {
  const events = useQuery({
    queryKey: ['events'],
    queryFn: () => api<EventSummary[]>('/api/events'),
  })
  const follows = useQuery({
    queryKey: ['follows'],
    queryFn: () => api<FollowedEvent[]>('/api/follows'),
  })
  const [open, setOpen] = useState(false)
  const active = events.data?.filter((e) => e.status === 'active').length ?? 0
  const atLimit = active >= me.limits.eventsPerCreator

  return (
    <Page header={<CreatorHeader me={me} />}>
      <ChannelsNotice channels={me.channels} textsEnabled={me.textsEnabled} />
      {me.textsEnabled && <CreditBanner />}
      <div className="mb-5 flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Your events</h1>
          <p className="text-sm text-muted-foreground">
            {active} of {me.limits.eventsPerCreator} active events
          </p>
        </div>
        <Button onClick={() => setOpen(true)} disabled={atLimit}>
          <Plus /> New event
        </Button>
      </div>

      {events.data?.length === 0 && follows.data && follows.data.length > 0 && (
        <p className="text-muted-foreground">
          You haven’t started an event. Tap New event to plan one.
        </p>
      )}
      {events.data?.length === 0 && follows.data?.length === 0 && (
        <Card className="text-center">
          <p className="text-lg font-medium">No events yet</p>
          <p className="mt-1 text-muted-foreground">
            Start with a name, like “House move” or “Product launch”.
          </p>
          <Button className="mt-4" onClick={() => setOpen(true)}>
            <Plus /> Create your first event
          </Button>
        </Card>
      )}

      <ul className="space-y-3">
        {events.data?.map((e) => (
          <li key={e.id}>
            <Link
              to="/events/$eventId"
              params={{ eventId: String(e.id) }}
              className="flex items-center gap-4 rounded-2xl border bg-card p-4 shadow-xs transition-colors hover:bg-accent"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="min-w-0 text-lg font-medium break-words">{e.title}</span>
                  {e.pending > 0 && (
                    <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground">
                      {e.pending} to approve
                    </span>
                  )}
                  {e.status === 'archived' && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      Archived
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <CalendarCheck className="size-4" aria-hidden />
                    {e.stepsDone} of {e.stepsTotal} steps
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Users className="size-4" aria-hidden />
                    {e.contactsActive} following
                  </span>
                </div>
                {e.stepsTotal > 0 && (
                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-done"
                      style={{ width: `${(e.stepsDone / e.stepsTotal) * 100}%` }}
                    />
                  </div>
                )}
              </div>
              <ChevronRight className="size-5 text-muted-foreground" aria-hidden />
            </Link>
          </li>
        ))}
      </ul>
      {atLimit && (
        <p className="mt-4 text-sm text-muted-foreground">
          You’ve reached the free limit of {me.limits.eventsPerCreator} active events. Archive one
          to start another.
        </p>
      )}
      {follows.data && <FollowedEvents follows={follows.data} me={me} />}
      <NewEventDialog open={open} onOpenChange={setOpen} me={me} />
      <Footer />
    </Page>
  )
}

/**
 * "Events you follow": events where the person signed up with this account's verified email,
 * or added from a personal link. Pending requests wait; left and ended events sit under Past.
 */
function FollowedEvents({ follows, me }: { follows: FollowedEvent[]; me: MeResponse }) {
  const current = follows.filter((f) => f.status !== 'left' && f.eventStatus === 'active')
  const past = follows.filter((f) => f.status === 'left' || f.eventStatus !== 'active')
  return (
    <section aria-labelledby="following" className="mt-10">
      <h2 id="following" className="text-2xl font-semibold">
        Events you follow
      </h2>
      {current.length === 0 && (
        <p className="mt-1 text-sm text-muted-foreground">
          {me.user?.email && me.user.emailVerified
            ? `Events you sign up for with ${me.user.email} show up here.`
            : 'Verify the email address on your account to see the events you’ve signed up for.'}
        </p>
      )}
      <ul className="mt-4 space-y-3">
        {current.map((f) => (
          <li key={f.eventId}>
            <FollowedCard f={f} />
          </li>
        ))}
      </ul>
      {past.length > 0 && (
        <details className="group mt-4">
          <summary className="inline-flex min-h-10 cursor-pointer list-none items-center gap-1 text-muted-foreground hover:text-foreground">
            <ChevronRight
              className="size-4 transition-transform group-open:rotate-90"
              aria-hidden
            />
            Past ({past.length})
          </summary>
          <ul className="mt-2 space-y-3">
            {past.map((f) => (
              <li key={f.eventId}>
                <FollowedCard f={f} />
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}

function FollowedCard({ f }: { f: FollowedEvent }) {
  const label =
    f.status === 'pending'
      ? { text: 'Waiting for approval', className: 'bg-waiting-soft text-foreground' }
      : f.status === 'invited'
        ? { text: 'Invited', className: 'bg-primary text-primary-foreground' }
        : f.status === 'left'
          ? { text: 'Left', className: 'bg-muted text-muted-foreground' }
          : f.eventStatus !== 'active'
            ? { text: 'Ended', className: 'bg-muted text-muted-foreground' }
            : { text: 'Joined', className: 'bg-done-soft text-foreground' }
  const body = (
    <>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="min-w-0 text-lg font-medium break-words">{f.title}</span>
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${label.className}`}>
            {label.text}
          </span>
        </div>
        <p className="mt-0.5 text-sm text-muted-foreground">Updates from {f.senderName}</p>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <CalendarCheck className="size-4" aria-hidden />
            {/* Hidden steps are not counted until sent: all done here may not be the end. */}
            {f.stepsDone < f.stepsTotal
              ? `${f.stepsDone} of ${f.stepsTotal} ${f.stepsTotal === 1 ? 'step' : 'steps'}`
              : `${f.stepsDone} ${f.stepsDone === 1 ? 'step' : 'steps'} done`}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="size-4" aria-hidden />
            {f.lastUpdateAt ? `Updated ${timeOf(f.lastUpdateAt)}` : 'No updates yet'}
          </span>
        </div>
        {f.status === 'invited' && (
          <p className="mt-1 text-sm text-muted-foreground">
            You were added to this event. Join to get updates.
          </p>
        )}
      </div>
      {(f.status === 'joined' || f.status === 'invited') && (
        <ChevronRight className="size-5 text-muted-foreground" aria-hidden />
      )}
    </>
  )
  const box = 'flex items-center gap-4 rounded-2xl border bg-card p-4 shadow-xs'
  if (f.status === 'joined') {
    return (
      <Link
        to="/e/$eventId"
        params={{ eventId: String(f.eventId) }}
        className={`${box} transition-colors hover:bg-accent`}
      >
        {body}
      </Link>
    )
  }
  if (f.status === 'invited' && f.joinCode) {
    return (
      <Link
        to="/join/$code"
        params={{ code: f.joinCode }}
        className={`${box} transition-colors hover:bg-accent`}
      >
        {body}
      </Link>
    )
  }
  return <div className={box}>{body}</div>
}

function NewEventDialog({
  open,
  onOpenChange,
  me,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  me: MeResponse
}) {
  const navigate = useNavigate()
  const [title, setTitle] = useState('')
  const [template, setTemplate] = useState<EventTemplate | null>(null)
  const [picked, setPicked] = useState(false)
  const [senderName, setSenderName] = useState(me.user?.name?.split(' ')[0] ?? '')
  const [error, setError] = useState<{ field?: string; message: string } | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      // A template's steps are created with the event, in the same request.
      const { id } = await api<{ id: number }>('/api/events', {
        body: { title, senderName, ...(template ? { steps: template.steps } : {}) },
      })
      onOpenChange(false)
      await navigate({ to: '/events/$eventId', params: { eventId: String(id) } })
    } catch (err) {
      setError(
        err instanceof ApiFailure
          ? { field: err.field, message: err.message }
          : { message: 'Try again.' },
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New event</DialogTitle>
          <DialogDescription>
            The event name appears in every {me.textsEnabled ? 'text' : 'update'}, so keep it short.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit}>
          <div className="space-y-2">
            <TemplateChips
              withBlank
              selected={template ? template.id : picked ? 'blank' : null}
              onPick={(t) => {
                // The name follows the template unless the organizer typed their own.
                const typed = title.trim() && title !== template?.title
                if (!typed) setTitle(t?.title ?? '')
                setTemplate(t)
                setPicked(true)
              }}
            />
            {template && <TemplatePreview template={template} />}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="event-title">Event name</Label>
            <Input
              id="event-title"
              value={title}
              maxLength={MAX_EVENT_TITLE}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="House move"
              required
              aria-invalid={error?.field === 'title' || undefined}
            />
            {error?.field === 'title' && <FieldError>{error.message}</FieldError>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sender-name">
              {me.textsEnabled ? 'Texts come from' : 'Updates come from'}
            </Label>
            <Input
              id="sender-name"
              value={senderName}
              maxLength={MAX_SENDER_NAME}
              onChange={(e) => setSenderName(e.target.value)}
              placeholder="Your first name"
              required
              aria-invalid={error?.field === 'senderName' || undefined}
            />
            <p className="text-sm text-muted-foreground">
              The join page reads: “Updates from {senderName || 'Sam'}, sent by {APP_NAME}.”
            </p>
            {error?.field === 'senderName' && <FieldError>{error.message}</FieldError>}
          </div>
          {error && !error.field && <FieldError>{error.message}</FieldError>}
          <DialogFooter>
            <Button type="submit" size="lg" disabled={busy} className="w-full sm:w-auto">
              Create event
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
